import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, renderList as _renderList, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  class: "m-auto",
  "data-test-ref": "downtime-alert"
}
const _hoisted_2 = { class: "d-flex align-items-center position-relative" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "px-2 flex-grow-0" }, [
  /*#__PURE__*/_createElementVNode("i", {
    class: "fa-solid fa-triangle-exclamation",
    "data-test-ref": "multi-factor-auth-session-expired-icon"
  })
], -1 /* HOISTED */)
const _hoisted_4 = { class: "px-2 flex-grow-1 text-dark" }
const _hoisted_5 = { "data-test-ref": "alert-description" }
const _hoisted_6 = { "data-test-ref": "multi-factor-auth-personal-data-safe" }
const _hoisted_7 = { "data-test-ref": "multi-factor-auth-send-notification" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("i", {
  class: "fas fa-exclamation-circle mr-2",
  "aria-hidden": "true",
  "data-test-ref": "multi-factor-auth-code-generation-error-icon"
}, null, -1 /* HOISTED */)
const _hoisted_9 = {
  class: "mb-0",
  "data-test-ref": "multi-factor-auth-code-generation-error-text"
}
const _hoisted_10 = {
  class: "font-weight-semi-bold",
  "data-test-ref": "multi-factor-auth-code-options-heading"
}
const _hoisted_11 = {
  class: "list-unstyled mb-0",
  "data-test-ref": "multi-factor-auth-list"
}
const _hoisted_12 = ["data-test-ref"]
const _hoisted_13 = ["data-test-ref"]
const _hoisted_14 = ["data-test-ref"]
const _hoisted_15 = /*#__PURE__*/_createElementVNode("hr", { "data-test-ref": "multi-factor-auth-system-error-divider" }, null, -1 /* HOISTED */)
const _hoisted_16 = {
  class: "text-center p-5 font-weight-bold",
  "data-test-ref": "multi-factor-auth-summary-system-error"
}
const _hoisted_17 = {
  class: "d-flex justify-content-center font-weight-bold",
  "data-test-ref": "multi-factor-auth-system-error-header-container"
}
const _hoisted_18 = /*#__PURE__*/_createElementVNode("i", {
  "data-test-ref": "multi-factor-auth-summary-system-error-message-icon",
  class: "fa-2x fas fa-exclamation-circle text-danger ml-2 mr-1"
}, null, -1 /* HOISTED */)
const _hoisted_19 = {
  class: "text-primary-dark font-weight-bold text-center",
  "data-test-ref": "multi-factor-auth-summary-system-error-message-header"
}
const _hoisted_20 = {
  class: "h4 text-primary-dark mt-1 font-weight-bold text-center",
  "data-test-ref": "multi-factor-auth-summary-system-error-message1"
}
const _hoisted_21 = {
  class: "h4 text-primary-dark mt-1 font-weight-bold text-center",
  "data-test-ref": "multi-factor-auth-summary-system-error-message2"
}
const _hoisted_22 = /*#__PURE__*/_createElementVNode("hr", { "data-test-ref": "multi-factor-auth-system-error-divider" }, null, -1 /* HOISTED */)

import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from '@/i18n';
import * as logger from 'commercial-web-client-utilities/dist/logging/logger';
import { useNonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import { useLedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { ContactChannelsStatusCode, ContactChannelTypes } from '@/enums/multi-factor-auth';
import { ContactChannel } from '@/types/mfa';
import { createMemberMfaService } from '@/composables/multi-factor-auth-service';
import { AxiosError } from 'axios';
import { useAppStore, useMultiFactorAuthStore } from '@/store';
import { deviceInfoService } from '@/composables/device-info-service';


export default /*#__PURE__*/_defineComponent({
  __name: 'multi-factor-auth-contact-channels',
  setup(__props) {

const nonSecureService = useNonsecureService();
const ledsService = useLedsService();
const memberMfaService = createMemberMfaService({
    nonsecureService: nonSecureService,
    ledsService: ledsService
});
const { t } = useI18n();
const appStore = useAppStore();
const router = useRouter();
const mfaOptions = ref<ContactChannel[]>([]);
const loading = ref<boolean>(false);
const isCodeGenerateError = ref<boolean>(false);
const isSystemError = ref<boolean>(false);
const isSessionExpired = ref<boolean>(false);
const multiFactorAuthStore = useMultiFactorAuthStore();

onMounted(async () => {
    try {
        mfaOptions.value = await getMfaChannels();
    } catch (error) {
        logger.error('Failed to load MFA options in onMounted:', error);
        mfaOptions.value = []; // Set fallback state here
        isSystemError.value = true;
    }
});

const displayTextForChannel = computed(() => {
    return (channel: ContactChannel) => {
        const typeOfChannel = channel.contactChannelType;
        switch (typeOfChannel) {
            case 'EMAIL':
                return t('mfa.contactChannels.email', { email: channel.displayText });
            case 'VOICE':
                return t('mfa.contactChannels.voice', { voice: channel.displayText });
            case 'TEXT':
                return t('mfa.contactChannels.text', { text: channel.displayText });
            default:
                return channel.displayText;
        }
    };
});

const getContactChannelIcon = (contactChannelType: string): string => {
    if (ContactChannelTypes.EMAIL.toLowerCase() === contactChannelType.toLowerCase()) {
        return 'envelope';
    } else if (ContactChannelTypes.VOICE.toLowerCase() === contactChannelType.toLowerCase()) {
        return 'phone fa-flip-horizontal';
    } else {
        return 'comment-dots';
    }
};

const resetFlags = () => {
    isCodeGenerateError.value = false;
    isSystemError.value = false;
    isSessionExpired.value = false;
};

const getMfaChannels = async (): Promise<ContactChannel[]> => {
    loading.value = true;
    resetFlags();
    try {
        const mfaToken = sessionStorage.getItem('mfaToken') ?? '';
        multiFactorAuthStore.setMultifactorAuthToken(mfaToken);
        const mfaResponse = await memberMfaService.getMemberContactChannels({
            signal: new AbortController().signal,
            mfaToken: mfaToken
        });
        isSystemError.value = false;
        if (mfaResponse.data.mfaToken) multiFactorAuthStore.setMultifactorAuthToken(mfaResponse.data.mfaToken);
        return mfaResponse.data.contactChannels;
    } catch (axiosError) {
        const error = axiosError as AxiosError;
        const errorData = error.response?.status;
        if (errorData === ContactChannelsStatusCode.MULTI_FACTOR_SESSION_EXPIRED) {
            isSessionExpired.value = true;
            logger.error('session expired error');
        } else {
            logger.error('error while fetching contact channels');
            isSystemError.value = true;
        }
        return [];
    } finally {
        loading.value = false;
    }
};

const generateOneTimePassword = async (mfaSelected: ContactChannel) => {
    loading.value = true;
    const channelId: string = mfaSelected.channelId ?? '';
    resetFlags();
    try {
        const deviceInfo = await deviceInfoService.getDeviceInfo();
        const payload = {
            channelId: channelId,
            deviceInfo
        };
        const mfaResponse = await memberMfaService.requestMemberOneTimePassword({
            signal: new AbortController().signal,
            mfaToken: multiFactorAuthStore.multifactorAuthToken,
            payload: payload
        });
        logger.log(mfaResponse, 'mfaResponse');
        multiFactorAuthStore.reset();
        multiFactorAuthStore.setSelectedContactChannel(mfaSelected);
        multiFactorAuthStore.setMultifactorAuthToken(mfaResponse.data.mfaToken);
        isCodeGenerateError.value = false;
        navigateToMultiFactorAuthCode();
    } catch (error) {
        const errorData = (error as AxiosError)?.response;
        logger.error(errorData?.status, 'errorData');
        if (errorData?.status === 437) {
            isSessionExpired.value = true;
        } else {
            isCodeGenerateError.value = true;
        }
    } finally {
        loading.value = false;
    }
};

const tryAgain = async () => {
    resetFlags();
    try {
        mfaOptions.value = await getMfaChannels();
    } catch (error) {
        logger.error('Failed to load MFA options in onMounted:', error);
        mfaOptions.value = [];
        isSystemError.value = true;
    }
};

const navigateToLogin = () => {
    window.location.href = appStore.logoutUrl;
};

const navigateToMultiFactorAuthCode = () => {
    router.push({
        name: 'multi-factor-auth-code',
    });
};

return (_ctx: any,_cache: any) => {
  const _component_b_alert = _resolveComponent("b-alert")!
  const _component_b_button = _resolveComponent("b-button")!
  const _component_b_row = _resolveComponent("b-row")!
  const _component_b_container = _resolveComponent("b-container")!
  const _component_b_overlay = _resolveComponent("b-overlay")!

  return (_openBlock(), _createBlock(_component_b_overlay, {
    show: loading.value,
    variant: "light",
    opacity: "0.75",
    rounded: "sm",
    class: "mt-2",
    "spinner-variant": "primary",
    "data-test-ref": "multi-factor-auth-overlay"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_b_container, { "data-test-ref": "multi-factor-auth-contact-channel-container" }, {
        default: _withCtx(() => [
          (!isSystemError.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_b_row, {
                  class: "flex-column pt-3 pb-3",
                  "data-test-ref": "multi-factor-auth-channels-options"
                }, {
                  default: _withCtx(() => [
                    (isSessionExpired.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createCommentVNode(" Session Expired Warning "),
                          _createVNode(_component_b_alert, {
                            variant: "warning",
                            show: isSessionExpired.value,
                            "data-test-ref": "multi-factor-auth-session-expied-alert",
                            class: "mb-2 align-middle rounded-4 border-warning"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_1, [
                                _createElementVNode("div", _hoisted_2, [
                                  _hoisted_3,
                                  _createElementVNode("div", _hoisted_4, [
                                    _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('mfa.error.sessionExpired')), 1 /* TEXT */)
                                  ])
                                ])
                              ])
                            ], undefined, true),
                            _: 1 /* STABLE */
                          }, 8 /* PROPS */, ["show"]),
                          _createElementVNode("p", null, _toDisplayString(_unref(t)('mfa.warning.keepYourDataSafe')), 1 /* TEXT */)
                        ], 64 /* STABLE_FRAGMENT */))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)('mfa.warning.personalDataSafe')), 1 /* TEXT */),
                          _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)('mfa.warning.sendNotification')), 1 /* TEXT */),
                          _createCommentVNode(" Code Generation Error Alert "),
                          (isCodeGenerateError.value)
                            ? (_openBlock(), _createBlock(_component_b_alert, {
                                key: 0,
                                show: "",
                                variant: "danger",
                                "data-test-ref": "multi-factor-auth-code-generation-error-alert",
                                class: "d-flex align-items-center"
                              }, {
                                default: _withCtx(() => [
                                  _hoisted_8,
                                  _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)('mfa.error.codeRequestError')), 1 /* TEXT */)
                                ], undefined, true),
                                _: 1 /* STABLE */
                              }))
                            : _createCommentVNode("v-if", true),
                          _createElementVNode("h3", _hoisted_10, _toDisplayString(_unref(t)('mfa.title.codeOptionsHeading')), 1 /* TEXT */),
                          _createElementVNode("ul", _hoisted_11, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mfaOptions.value, (mfa) => {
                              return (_openBlock(), _createElementBlock("li", {
                                key: mfa.contactChannelType,
                                "data-test-ref": `multi-factor-auth-item-${mfa.contactChannelType}`
                              }, [
                                _createVNode(_component_b_button, {
                                  block: "",
                                  variant: "link",
                                  class: "text-left p-3 border rounded mb-2 d-flex flex-column align-items-start cursor-pointer mhtk-mfa-contact-options",
                                  "data-test-ref": `multi-factor-auth-channel-
                                    ${mfa.contactChannelType.toLowerCase()}`,
                                  onClick: ($event: any) => (generateOneTimePassword(mfa))
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("span", {
                                      class: "ml-2",
                                      "data-test-ref": `multi-factor-auth-content-
                                    ${mfa.contactChannelType}`
                                    }, [
                                      _createElementVNode("i", {
                                        class: _normalizeClass(`fas fa-${getContactChannelIcon(mfa.contactChannelType)}
                                            fa-lg mr-2`),
                                        "data-test-ref": `multi-factor-auth-icon-${mfa.contactChannelType}`
                                      }, null, 10 /* CLASS, PROPS */, _hoisted_14),
                                      _createTextVNode(" " + _toDisplayString(_unref(displayTextForChannel)(mfa)), 1 /* TEXT */)
                                    ], 8 /* PROPS */, _hoisted_13)
                                  ], undefined, true),
                                  _: 2 /* DYNAMIC */
                                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["data-test-ref", "onClick"])
                              ], 8 /* PROPS */, _hoisted_12))
                            }), 128 /* KEYED_FRAGMENT */))
                          ])
                        ], 64 /* STABLE_FRAGMENT */))
                  ], undefined, true),
                  _: 1 /* STABLE */
                }),
                _hoisted_15
              ], 64 /* STABLE_FRAGMENT */))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createCommentVNode(" System Error State "),
                _createCommentVNode(" Error Message Display "),
                _createVNode(_component_b_row, {
                  class: "d-flex justify-content-center mt-4 mb-4",
                  "data-test-ref": "multi-factor-auth-system-error-row"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", _hoisted_17, [
                        _hoisted_18,
                        _createElementVNode("h3", _hoisted_19, _toDisplayString(_unref(t)('mfa.error.systemError.header')), 1 /* TEXT */)
                      ]),
                      _createElementVNode("div", _hoisted_20, _toDisplayString(_unref(t)('mfa.error.systemError.messageOne')), 1 /* TEXT */),
                      _createElementVNode("div", _hoisted_21, _toDisplayString(_unref(t)('mfa.error.systemError.messageTwo')), 1 /* TEXT */)
                    ])
                  ], undefined, true),
                  _: 1 /* STABLE */
                }),
                _createCommentVNode(" Divider "),
                _hoisted_22,
                _createCommentVNode(" Action Buttons "),
                _createVNode(_component_b_row, {
                  class: "d-flex justify-content-end mt-4 mb-4",
                  "data-test-ref": "multi-factor-auth-buttons"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_b_button, {
                      variant: "outline-primary",
                      class: "mr-2",
                      "data-test-ref": "multi-factor-auth-back-button",
                      onClick: navigateToLogin
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('mfa.action.returnToLogIn')), 1 /* TEXT */)
                      ], undefined, true),
                      _: 1 /* STABLE */
                    }),
                    _createVNode(_component_b_button, {
                      variant: "primary",
                      "data-test-ref": "multi-factor-auth-continue-button",
                      onClick: tryAgain
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('mfa.action.tryAgain')), 1 /* TEXT */)
                      ], undefined, true),
                      _: 1 /* STABLE */
                    })
                  ], undefined, true),
                  _: 1 /* STABLE */
                })
              ], 64 /* STABLE_FRAGMENT */))
        ], undefined, true),
        _: 1 /* STABLE */
      })
    ], undefined, true),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show"]))
}
}

})